import { useEffect, useState } from "react";

import { getMortgageRate } from "../api/client";
import type { ClosingCost } from "../types/api";
import { formatPrice } from "../utils/price";

/**
 * CostToOwnPanel — Task 9.7a follow-up. The Pro answer to a listing's
 * "estimated monthly payment": a full monthly cost-to-own that includes
 * mortgage P&I (adjustable down payment / rate / term, recomputed live
 * client-side) PLUS our data-grounded tax + insurance (flood/climate-aware)
 * + HOA. The interest-rate default is the current market average (FRED),
 * fetched once on mount, then editable.
 */

interface Props {
  askingPrice: number;
  closingCost: ClosingCost;
  scoreId?: string;
}

function monthlyPI(loan: number, annualRatePct: number, termYears: number): number {
  const r = annualRatePct / 100 / 12;
  const n = termYears * 12;
  if (loan <= 0 || n <= 0) return 0;
  if (r === 0) return loan / n;
  const f = Math.pow(1 + r, n);
  return (loan * (r * f)) / (f - 1);
}

function dollars(n: number): string {
  return formatPrice(Math.round(n));
}

export function CostToOwnPanel({ askingPrice, closingCost, scoreId }: Props) {
  // Editable home price — defaults to the asking price, but the buyer can
  // lower it to their target offer to model the payment at that number.
  const [price, setPrice] = useState(askingPrice);
  const [downPct, setDownPct] = useState(20);
  const [rate, setRate] = useState(6.5);
  const [rateAsOf, setRateAsOf] = useState<string | null>(null);
  const [term, setTerm] = useState(30);
  const [hoaOverride, setHoaOverride] = useState<number | null>(() => {
    if (!scoreId) return null;
    try {
      const stored = localStorage.getItem(`pt.hoa.${scoreId}`);
      return stored ? Number(stored) : null;
    } catch {
      return null;
    }
  });

  // Persist HOA to localStorage when the user changes it.
  useEffect(() => {
    if (!scoreId) return;
    try {
      if (hoaOverride != null && hoaOverride > 0) {
        localStorage.setItem(`pt.hoa.${scoreId}`, String(hoaOverride));
      } else {
        localStorage.removeItem(`pt.hoa.${scoreId}`);
      }
    } catch {
      /* localStorage unavailable */
    }
  }, [hoaOverride, scoreId]);

  // Reset the modeled price when the property changes (new score).
  useEffect(() => {
    setPrice(askingPrice);
  }, [askingPrice]);

  useEffect(() => {
    let cancelled = false;
    getMortgageRate()
      .then((r) => {
        if (!cancelled) {
          setRate(r.rate);
          setRateAsOf(r.as_of);
        }
      })
      .catch(() => {
        /* keep the 6.5% default */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loan = price * (1 - downPct / 100);
  const pi = monthlyPI(loan, rate, term);

  const ins =
    closingCost.monthly_insurance_low != null && closingCost.monthly_insurance_high != null
      ? (closingCost.monthly_insurance_low + closingCost.monthly_insurance_high) / 2
      : closingCost.monthly_insurance_high ?? closingCost.monthly_insurance_low ?? 0;
  const tax = closingCost.monthly_tax_forecast ?? 0;
  const hoa = hoaOverride ?? closingCost.monthly_hoa ?? 0;
  const total = pi + ins + tax + hoa;
  const downPayment = price * (downPct / 100);
  const belowAsking = price < askingPrice;

  return (
    <section className="cost-to-own" aria-label="Estimated monthly cost to own">
      <header className="cto-head">
        <h3>Estimated monthly cost to own</h3>
        <div className="cto-total">
          <span className="cto-total-value">{dollars(total)}</span>
          <span className="cto-total-unit">/mo</span>
        </div>
      </header>

      <div className="cto-controls">
        <label className="cto-inline-field cto-price-field">
          Home price
          <input
            type="text"
            inputMode="numeric"
            value={price ? formatPrice(price) : ""}
            onChange={(e) => {
              const digits = e.target.value.replace(/[^0-9]/g, "");
              setPrice(digits ? Number(digits) : 0);
            }}
            aria-label="Home price — lower it to model your offer"
          />
          {belowAsking && <span className="cto-price-note">your offer</span>}
        </label>
        <label>
          Down payment
          <span className="cto-control-row">
            <input
              type="range"
              min={0}
              max={50}
              step={1}
              value={downPct}
              onChange={(e) => setDownPct(Number(e.target.value))}
              aria-label="Down payment percent"
            />
            <span className="cto-control-val">{downPct}% · {dollars(downPayment)}</span>
          </span>
        </label>
        <label className="cto-inline-field">
          Rate
          <input
            type="number"
            min={0}
            max={20}
            step={0.01}
            value={rate}
            onChange={(e) => setRate(Number(e.target.value))}
            aria-label="Interest rate percent"
          />
          %
        </label>
        <label className="cto-inline-field">
          Term
          <select value={term} onChange={(e) => setTerm(Number(e.target.value))} aria-label="Loan term years">
            <option value={30}>30 yr</option>
            <option value={20}>20 yr</option>
            <option value={15}>15 yr</option>
          </select>
        </label>
      </div>

      <dl className="cto-breakdown">
        <div><dt>Mortgage (P&amp;I)</dt><dd>{dollars(pi)}/mo</dd></div>
        <div><dt>Property tax</dt><dd>{tax ? `${dollars(tax)}/mo` : "—"}</dd></div>
        <div><dt>Insurance</dt><dd>{ins ? `${dollars(ins)}/mo` : "—"}</dd></div>
        <div><dt>HOA</dt><dd>
          <label className="cto-inline-field cto-hoa-field">
            <span className="cto-hoa-dollar">$</span>
            <input
              type="text"
              inputMode="numeric"
              placeholder="0"
              value={hoa || ""}
              onChange={(e) => {
                const digits = e.target.value.replace(/[^0-9]/g, "");
                setHoaOverride(digits ? Number(digits) : 0);
              }}
              aria-label="Monthly HOA fee — enter from listing"
              className="cto-hoa-input"
            />
            <span className="cto-hoa-suffix">/mo</span>
          </label>
        </dd></div>
      </dl>

      <p className="cto-foot">
        Based on {formatPrice(price)}{belowAsking ? ` (asking ${formatPrice(askingPrice)})` : ""} at {rate}% / {term}-yr, {downPct}% down
        {rateAsOf ? ` · rate: Freddie Mac avg as of ${rateAsOf}` : ""}.
        {closingCost.volatility_message ? ` ${closingCost.volatility_message}` : ""}{" "}
        {closingCost.disclaimer}
      </p>
    </section>
  );
}
